/**
 * ClientsSection Component
 *
 * Displays client analytics including stat cards, growth chart,
 * and distribution charts (by source and status).
 */

import { Box, Typography, Grid, Paper } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import StatCard from './StatCard';
import EmptyState from './EmptyState';
import { SectionSkeleton } from './AnalyticsSkeleton';
import { ANALYTICS_SPACING } from './constants';
import { ClientStats } from '../../services/analyticsApi';

interface ClientsSectionProps {
  data: ClientStats | null;
  loading?: boolean;
}

// Colors for status distribution
const STATUS_COLORS: Record<string, string> = {
  active: '#10B981', // Green
  inactive: '#9CA3AF', // Gray
  lead: '#3B82F6', // Blue
  archived: '#6B7280', // Dark gray
};

// Colors for source distribution
const SOURCE_COLORS = [
  '#5D5FEF', // Elevair purple
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
];

/**
 * Format source labels for display
 */
function formatSource(source: string): string {
  const labels: Record<string, string> = {
    referral: 'Referral',
    social_media: 'Social Media',
    website: 'Website',
    landing_page: 'Landing Page',
    platform_signup: 'Platform Signup',
    manual: 'Manual Entry',
    other: 'Other',
    unknown: 'Unknown',
  };
  return labels[source] || source.charAt(0).toUpperCase() + source.slice(1).replace(/_/g, ' ');
}

/**
 * Format status labels for display
 */
function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Custom tooltip for line chart
 */
function LineChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (active && payload && payload.length && payload[0]) {
    const date = new Date(label || '');
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return (
      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {formattedDate}
        </Typography>
        <Typography variant="body2" color="primary">
          {payload[0].value} new client{payload[0].value !== 1 ? 's' : ''}
        </Typography>
      </Paper>
    );
  }
  return null;
}

/**
 * Custom tooltip for pie charts
 */
function PieChartTooltip({
  active,
  payload,
  formatLabel,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number }>;
  formatLabel: (name: string) => string;
}) {
  if (active && payload && payload.length && payload[0]) {
    return (
      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {formatLabel(payload[0].name)}
        </Typography>
        <Typography variant="body2" color="primary">
          {payload[0].value} client{payload[0].value !== 1 ? 's' : ''}
        </Typography>
      </Paper>
    );
  }
  return null;
}

export default function ClientsSection({ data, loading = false }: ClientsSectionProps) {
  // Show skeleton while loading
  if (loading) {
    return <SectionSkeleton />;
  }

  // Check for empty data state
  const isEmpty = !data || data.stats?.totalClients === 0;

  if (isEmpty) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: ANALYTICS_SPACING.titleMarginBottom }}>
          Clients
        </Typography>
        <EmptyState type="clients" />
      </Box>
    );
  }

  // Transform chart data for display
  const growthData =
    data?.charts?.clientGrowth?.map((item) => ({
      date: item.date,
      count: item.count,
    })) || [];

  const sourceData =
    data?.charts?.bySource?.map((item) => ({
      name: item.source,
      value: item.count,
    })) || [];

  const statusData =
    data?.charts?.byStatus?.map((item) => ({
      name: item.status,
      value: item.count,
    })) || [];

  // Build accessible descriptions for charts
  const growthChartDescription =
    growthData.length > 0
      ? `Client growth chart showing new clients over time.`
      : 'No client growth data available';

  const statusChartDescription =
    statusData.length > 0
      ? `Clients by status: ${statusData.map((s) => `${formatStatus(s.name)}: ${s.value}`).join(', ')}`
      : 'No status data available';

  const sourceChartDescription =
    sourceData.length > 0
      ? `Clients by source: ${sourceData.map((s) => `${formatSource(s.name)}: ${s.value}`).join(', ')}`
      : 'No source data available';

  return (
    <Box>
      {/* Section Title */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: ANALYTICS_SPACING.titleMarginBottom }}>
        Clients
      </Typography>

      {/* Stat Cards Grid */}
      <Grid container spacing={ANALYTICS_SPACING.cardGap} sx={{ mb: ANALYTICS_SPACING.cardsToChartsGap }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Clients" value={data?.stats?.totalClients || 0} format="number" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="New This Period"
            value={data?.stats?.newThisPeriod || 0}
            format="number"
            subtitle="in selected range"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Lead Conversion"
            value={data?.stats?.leadConversionRate || 0}
            format="percentage"
            subtitle="leads → clients"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Clients" value={data?.stats?.activeClients || 0} format="number" />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={ANALYTICS_SPACING.chartGap}>
        {/* Client Growth Line Chart */}
        <Grid item xs={12} lg={8}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              Client Growth
            </Typography>
            <Box component="span" sx={visuallyHidden}>
              {growthChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              {growthData.length > 0 ? (
                <ResponsiveContainer>
                  <LineChart data={growthData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        });
                      }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                    />
                    <Tooltip content={<LineChartTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="New Clients"
                      stroke="#5D5FEF"
                      strokeWidth={2}
                      dot={{ fill: '#5D5FEF', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No client activity in this period</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Clients by Status Donut Chart */}
        <Grid item xs={12} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              By Status
            </Typography>
            <Box component="span" sx={visuallyHidden}>
              {statusChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              {statusData.length > 0 ? (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {statusData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={STATUS_COLORS[entry.name] || SOURCE_COLORS[index % SOURCE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<PieChartTooltip formatLabel={formatStatus} />} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      formatter={(value) => formatStatus(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No client data</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Clients by Source Pie Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              By Source
            </Typography>
            <Box component="span" sx={visuallyHidden}>
              {sourceChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              {sourceData.length > 0 ? (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={sourceData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value }) => `${formatSource(name || '')}: ${value}`}
                    >
                      {sourceData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieChartTooltip formatLabel={formatSource} />} />
                    <Legend formatter={(value) => formatSource(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No source data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
